/**
 * Accountant Profile Page
 * Public view of an accountant's profile and reviews
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { trpc } from "@/lib/trpc";
import {
  Building2,
  Star,
  MapPin,
  CheckCircle2,
  Globe,
  Mail,
  Phone,
  Users,
  Award,
  Briefcase,
  Calendar,
  ExternalLink,
  ArrowLeft,
  MessageSquare,
} from "lucide-react";
import { Link, useParams } from "wouter";

// Swiss cantons map
const SWISS_CANTONS: Record<string, string> = {
  ZH: "Zürich",
  BE: "Bern",
  LU: "Luzern",
  UR: "Uri",
  SZ: "Schwyz",
  OW: "Obwalden",
  NW: "Nidwalden",
  GL: "Glarus",
  ZG: "Zug",
  FR: "Fribourg",
  SO: "Solothurn",
  BS: "Basel-Stadt",
  BL: "Basel-Landschaft",
  SH: "Schaffhausen",
  AR: "Appenzell A.Rh.",
  AI: "Appenzell I.Rh.",
  SG: "St. Gallen",
  GR: "Graubünden",
  AG: "Aargau",
  TG: "Thurgau",
  TI: "Ticino",
  VD: "Vaud",
  VS: "Valais",
  NE: "Neuchâtel",
  GE: "Genève",
  JU: "Jura",
};

const SPECIALIZATIONS: Record<string, string> = {
  startups: "Startups & Scale-ups",
  "r&d": "R&D Tax Credits",
  international: "International Tax",
  vat: "VAT Specialist",
  payroll: "Payroll & HR",
  audit: "Audit Services",
  succession: "Business Succession",
  "real-estate": "Real Estate",
};

const CERTIFICATIONS: Record<string, string> = {
  expert_suisse: "Expert Suisse",
  treuhand_suisse: "Treuhand Suisse",
  cpa: "CPA",
  swiss_gaap: "Swiss GAAP FER",
};

const FIRM_TYPES: Record<string, string> = {
  solo: "Solo Practitioner",
  partnership: "Partnership",
  firm: "Accounting Firm",
};

export default function AccountantProfile() {
  return (
    <DashboardLayout>
      <ProfileContent />
    </DashboardLayout>
  );
}

function ProfileContent() {
  const { id } = useParams<{ id: string }>();
  const accountantId = parseInt(id || "0", 10);

  const {
    data: profile,
    isLoading,
    error,
  } = trpc.accountant.getProfile.useQuery(
    { id: accountantId },
    { enabled: accountantId > 0 }
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted rounded animate-pulse w-48" />
        <Card className="animate-pulse">
          <CardContent className="p-6">
            <div className="h-48 bg-muted rounded" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <Building2 className="h-16 w-16 text-muted-foreground" />
        <h2 className="text-2xl font-bold">Accountant not found</h2>
        <p className="text-muted-foreground">
          This accountant profile doesn't exist or is not public.
        </p>
        <Link href="/accountants">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Marketplace
          </Button>
        </Link>
      </div>
    );
  }

  const avgRating = profile.avgRating > 0 ? profile.avgRating / 100 : 0;

  return (
    <div className="space-y-6">
      {/* Back Link */}
      <Link href="/accountants">
        <Button variant="ghost" size="sm">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Marketplace
        </Button>
      </Link>

      {/* Profile Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar & Basic Info */}
            <div className="flex flex-col items-center md:items-start gap-4">
              <Avatar className="h-24 w-24">
                <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                  {profile.firmName.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {profile.acceptingClients && (
                <Badge className="bg-[var(--success)]/10 text-[var(--success)]">
                  Accepting Clients
                </Badge>
              )}
            </div>

            {/* Details */}
            <div className="flex-1 space-y-4">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-bold">{profile.firmName}</h1>
                  {profile.isVerified && (
                    <Badge
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      <CheckCircle2 className="h-3 w-3" />
                      Verified
                    </Badge>
                  )}
                </div>
                {profile.firmType && (
                  <p className="text-muted-foreground">
                    {FIRM_TYPES[profile.firmType] || profile.firmType}
                  </p>
                )}
              </div>

              {/* Stats Row */}
              <div className="flex flex-wrap gap-4 text-sm">
                {avgRating > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 fill-[var(--warning)] text-[var(--warning)]" />
                    <span className="font-medium">{avgRating.toFixed(1)}</span>
                    <span className="text-muted-foreground">
                      ({profile.reviewCount || 0} reviews)
                    </span>
                  </div>
                )}
                {profile.clientCount !== null && profile.clientCount > 0 && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>{profile.clientCount} clients</span>
                  </div>
                )}
                {profile.cantons && profile.cantons.length > 0 && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span>
                      {profile.cantons
                        .slice(0, 3)
                        .map((c: string) => SWISS_CANTONS[c] || c)
                        .join(", ")}
                      {profile.cantons.length > 3 &&
                        ` +${profile.cantons.length - 3}`}
                    </span>
                  </div>
                )}
                {profile.createdAt && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>
                      Member since{" "}
                      {new Date(profile.createdAt).toLocaleDateString("en-CH", {
                        year: "numeric",
                        month: "short",
                      })}
                    </span>
                  </div>
                )}
              </div>

              {/* Bio */}
              {profile.bio && (
                <p className="text-muted-foreground">{profile.bio}</p>
              )}

              {/* Contact Row */}
              <div className="flex flex-wrap gap-3">
                {profile.website && (
                  <a
                    href={
                      profile.website.startsWith("http")
                        ? profile.website
                        : `https://${profile.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" size="sm">
                      <Globe className="mr-2 h-4 w-4" />
                      Website
                      <ExternalLink className="ml-2 h-3 w-3" />
                    </Button>
                  </a>
                )}
                {profile.contactEmail && (
                  <a href={`mailto:${profile.contactEmail}`}>
                    <Button variant="outline" size="sm">
                      <Mail className="mr-2 h-4 w-4" />
                      Email
                    </Button>
                  </a>
                )}
                {profile.contactPhone && (
                  <a href={`tel:${profile.contactPhone}`}>
                    <Button variant="outline" size="sm">
                      <Phone className="mr-2 h-4 w-4" />
                      Call
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs: Expertise & Reviews */}
      <Tabs defaultValue="expertise" className="space-y-4">
        <TabsList>
          <TabsTrigger value="expertise">
            <Briefcase className="mr-2 h-4 w-4" />
            Expertise
          </TabsTrigger>
          <TabsTrigger value="reviews">
            <MessageSquare className="mr-2 h-4 w-4" />
            Reviews ({profile.reviewCount || 0})
          </TabsTrigger>
        </TabsList>

        {/* Expertise Tab */}
        <TabsContent value="expertise" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Specializations */}
            {profile.specializations && profile.specializations.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Specializations</CardTitle>
                  <CardDescription>Areas of expertise</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {profile.specializations.map((spec: string) => (
                      <Badge key={spec} variant="secondary">
                        {SPECIALIZATIONS[spec] || spec}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Industries */}
            {profile.industries && profile.industries.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Industries</CardTitle>
                  <CardDescription>Sector experience</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {profile.industries.map((industry: string) => (
                      <Badge key={industry} variant="outline">
                        {industry}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Certifications */}
            {profile.certifications && profile.certifications.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Award className="h-5 w-5" />
                    Certifications
                  </CardTitle>
                  <CardDescription>Professional qualifications</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {profile.certifications.map((cert: string) => (
                      <Badge key={cert} variant="default">
                        {CERTIFICATIONS[cert] || cert}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Service Areas */}
            {profile.cantons && profile.cantons.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Service Areas
                  </CardTitle>
                  <CardDescription>Cantons served</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {profile.cantons.map((canton: string) => (
                      <Badge key={canton} variant="outline">
                        {SWISS_CANTONS[canton] || canton}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Swiss UID */}
          {profile.swissUid && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Business Registration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Swiss UID:</span>
                  <code className="bg-muted px-2 py-1 rounded text-sm">
                    {profile.swissUid}
                  </code>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Reviews Tab */}
        <TabsContent value="reviews" className="space-y-4">
          <ReviewsList reviews={profile.reviews || []} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface Review {
  id: number;
  rating: number;
  reviewText: string | null;
  responseText: string | null;
  createdAt: Date;
  respondedAt: Date | null;
}

function ReviewsList({ reviews }: { reviews: Review[] }) {
  if (!reviews || reviews.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No reviews yet</h3>
          <p className="text-sm text-muted-foreground text-center max-w-md">
            Reviews from clients will appear here once they're submitted.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {reviews.map(review => (
        <Card key={review.id}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <Star
                    key={star}
                    className={`h-4 w-4 ${
                      star <= review.rating
                        ? "fill-[var(--warning)] text-[var(--warning)]"
                        : "text-muted-foreground"
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">
                {new Date(review.createdAt).toLocaleDateString("en-CH", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>
            {review.reviewText && (
              <p className="text-sm mb-4">{review.reviewText}</p>
            )}
            {review.responseText && (
              <div className="bg-muted/50 rounded-lg p-4 mt-4">
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  Response from accountant
                </p>
                <p className="text-sm">{review.responseText}</p>
                {review.respondedAt && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {new Date(review.respondedAt).toLocaleDateString("en-CH", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
